import { useState } from "react";
import { ChevronDown, ChevronRight, Paperclip } from "lucide-react";
import { Button } from "@/components/ui/button";
import { truncateText, type EmailContent } from "@forgepoint/shared";
import { AssetDownload } from "./AssetDownload";
import { BodyRenderer } from "./BodyRenderer";

const PREVIEW_LENGTH = 150;

/**
 * Displays expandable email content inline for activity events.
 * Shows subject, from, to, and body with expandable content.
 * HTML comments (e.g. Outlook style `<!-- p {margin-top:0} -->`) are stripped at render time.
 * When the email has associated assets (e.g. inline images moved to attachments),
 * they are displayed as downloadable items below the body.
 * @param emailContent - The email content to display
 */
export const EmailViewer = ({
  emailContent,
}: {
  emailContent: EmailContent;
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const { from, to, cc, bcc, subject, body: rawBody, assetIds } = emailContent;
  const body = rawBody ? stripHtmlComments(rawBody) : rawBody;
  const hasBody = body && body.length > 0;
  const truncatedBody = hasBody ? truncateText(body, PREVIEW_LENGTH) : null;
  const hasLongBody = hasBody && body.length > PREVIEW_LENGTH;
  const hasAssets = assetIds && assetIds.length > 0;
  const hasExpandableContent =
    hasLongBody ||
    (cc && cc.length > 0) ||
    (bcc && bcc.length > 0) ||
    hasAssets;

  const toDisplay = to?.join(", ");

  return (
    <div className="space-y-2">
      <div className="border rounded-md p-3 bg-muted/30 space-y-2 text-xs">
        {subject && (
          <div>
            <span className="font-semibold">Subject:</span>{" "}
            <span className="text-muted-foreground">{subject}</span>
          </div>
        )}
        {from && (
          <div>
            <span className="font-semibold">From:</span>{" "}
            <span className="text-muted-foreground">{from}</span>
          </div>
        )}
        {toDisplay && (
          <div>
            <span className="font-semibold">To:</span>{" "}
            <span className="text-muted-foreground">{toDisplay}</span>
          </div>
        )}
        {isExpanded && cc && cc.length > 0 && (
          <div>
            <span className="font-semibold">CC:</span>{" "}
            <span className="text-muted-foreground">{cc.join(", ")}</span>
          </div>
        )}
        {isExpanded && bcc && bcc.length > 0 && (
          <div>
            <span className="font-semibold">BCC:</span>{" "}
            <span className="text-muted-foreground">{bcc.join(", ")}</span>
          </div>
        )}
        {hasBody && (
          <div className={isExpanded ? "pt-2 border-t" : ""}>
            <BodyRenderer body={isExpanded ? body : (truncatedBody ?? "")} />
          </div>
        )}
        {isExpanded && hasAssets && (
          <div className="pt-2 border-t space-y-2">
            <div className="flex items-center gap-1.5 font-semibold">
              <Paperclip className="h-3 w-3" />
              Attachments ({assetIds.length})
            </div>
            <div className="flex flex-wrap gap-2">
              {assetIds.map((id) => (
                <AssetDownload key={id} assetId={id} />
              ))}
            </div>
          </div>
        )}
        {hasExpandableContent && (
          <div className={isExpanded ? "pt-2 border-t" : ""}>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs p-0 hover:bg-transparent"
              onClick={() => setIsExpanded((current) => !current)}
            >
              {isExpanded ? (
                <ChevronDown className="h-3 w-3 mr-1" />
              ) : (
                <ChevronRight className="h-3 w-3 mr-1" />
              )}
              {isExpanded ? "Hide email" : "View full email"}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Strips HTML comments from email body text (e.g. Outlook style comments).
 * @param text - The text that may contain HTML comments
 * @returns Text with HTML comments removed and excessive blank lines collapsed
 */
function stripHtmlComments(text: string): string {
  const HTML_COMMENT_REGEX = /<!--[\s\S]*?-->/g;
  let result = text;
  let previous = "";
  while (result !== previous) {
    previous = result;
    result = result.replace(HTML_COMMENT_REGEX, "");
  }
  return result.replace(/\n{3,}/g, "\n\n").trim();
}
